import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useColorMode, Box } from '@chakra-ui/react';
import { AiFillHeart, AiFillDelete, AiFillEdit } from 'react-icons/ai';
import { FaPlus, FaSearch, FaEye } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SafeContent from '../Securite/safeContent';

const BlogPage = () => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [likedBlogs, setLikedBlogs] = useState(new Set());
    const [isAdmin, setIsAdmin] = useState(false);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 6;

    const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

    const fetchBlogs = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/blog`);
            if (response.status === 200) {
                setBlogs(response.data);
                const liked = JSON.parse(localStorage.getItem('likedBlogs')) || [];
                setLikedBlogs(new Set(liked));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = JSON.parse(atob(token.split('.')[1]));
                setIsAdmin(!!decoded.adminId);
            } catch (e) {
                setIsAdmin(false);
            }
        }
    }, []);

    const toggleLike = async (blogId) => {
        const token = localStorage.getItem('token');
        const isLiked = likedBlogs.has(blogId);
        const newLikedBlogs = new Set(likedBlogs);
        try {
            if (isLiked) {
                newLikedBlogs.delete(blogId);
                await axios.patch(`${API_URL}/api/blog/${blogId}/unlike`, {}, { headers: { Authorization: `Bearer ${token}` } });
            } else {
                newLikedBlogs.add(blogId);
                await axios.patch(`${API_URL}/api/blog/${blogId}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
            }
            setLikedBlogs(newLikedBlogs);
            localStorage.setItem('likedBlogs', JSON.stringify([...newLikedBlogs]));
            setBlogs(prev => prev.map(b => b.id === blogId ? { ...b, likes: isLiked ? b.likes - 1 : (b.likes || 0) + 1 } : b));
        } catch (error) {
            console.error(error);
        }
    };

    const deleteBlog = async (blogId) => {
        if (!window.confirm("Supprimer l'article ?")) return;
        try {
            await axios.delete(`${API_URL}/api/blog/${blogId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
            setBlogs(prev => prev.filter(b => b.id !== blogId));
        } catch (error) {
            console.error(error);
        }
    };

    const filteredBlogs = blogs.filter(blog =>
        (blog.title + blog.description).toLowerCase().includes(filter.toLowerCase())
    );
    const recentBlogs = [...filteredBlogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const totalPages = Math.ceil(recentBlogs.length / ITEMS_PER_PAGE);
    const paginatedBlogs = recentBlogs.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
    const popularBlogs = [...filteredBlogs].sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 5);

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

    // Page 1 → first article gets featured layout; rest go in grid
    const featuredBlog = page === 1 && paginatedBlogs.length > 0 ? paginatedBlogs[0] : null;
    const gridBlogs = page === 1 && paginatedBlogs.length > 0 ? paginatedBlogs.slice(1) : paginatedBlogs;

    return (
        <Box className={isDark ? 'bg-[#0a0f16] min-h-screen' : 'bg-[#f8f9fc] min-h-screen'}>
            <Helmet>
                <title>Blog & Actualités — Hetep Iaout Services</title>
                <meta name="description" content="Conseils, actualités et ressources sur l'aide à domicile, le maintien à domicile des seniors et l'accompagnement du handicap par Hetep Iaout Services." />
                <link rel="canonical" href="https://www.hisvie.com/blog/" />
            </Helmet>

            <main className="max-w-[1400px] mx-auto px-4 md:px-8 pt-28 md:pt-40 pb-20 md:pb-40">

                {/* ── Header ── */}
                <header className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16 md:mb-20">
                    <motion.span
                        initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                        className={`inline-block px-4 py-1.5 rounded-full text-[11px] font-bold tracking-widest uppercase mb-5 ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}
                    >
                        Blog & Actualités
                    </motion.span>

                    <motion.h1
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                        className={`text-4xl md:text-6xl font-bold mb-5 tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}
                    >
                        Nos dernières actualités
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className={`text-base md:text-lg mb-10 max-w-xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                        Conseils, témoignages et actualités sur l'aide à domicile, le handicap et l'accompagnement des aînés.
                    </motion.p>

                    {/* Search */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                        className={`flex w-full max-w-md items-center rounded-2xl p-1.5 border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}
                    >
                        <div className={`pl-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <FaSearch size={13} />
                        </div>
                        <input
                            type="text"
                            placeholder="Rechercher un article..."
                            value={filter}
                            onChange={(e) => { setFilter(e.target.value); setPage(1); }}
                            className={`flex-1 bg-transparent border-none px-4 py-2.5 text-sm focus:outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'}`}
                        />
                        <button className="bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors">
                            Rechercher
                        </button>
                    </motion.div>

                    {isAdmin && (
                        <Link to="/ajouter-article" className="mt-6">
                            <button className={`font-bold py-3 px-7 rounded-xl shadow-sm flex items-center gap-2 hover:scale-105 transition-transform text-sm ${isDark ? 'bg-white text-gray-900' : 'bg-gray-900 text-white'}`}>
                                <FaPlus size={12} /> Nouvel Article
                            </button>
                        </Link>
                    )}
                </header>

                {/* ── Body ── */}
                {loading ? (
                    <div className="flex justify-center py-32">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : paginatedBlogs.length === 0 ? (
                    <div className={`text-center py-24 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        <p className="text-xl font-light">Aucun article trouvé.</p>
                        {filter && (
                            <button onClick={() => setFilter('')} className="mt-4 text-blue-600 text-sm font-semibold hover:underline">
                                Effacer la recherche
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col xl:flex-row gap-10 xl:gap-14">

                        {/* ── Main column (75%) ── */}
                        <div className="w-full xl:w-3/4">

                            {/* Featured article */}
                            {featuredBlog && (
                                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10 group">
                                    <Link
                                        to={`/blog/${featuredBlog.id}`}
                                        className={`flex flex-col md:flex-row rounded-3xl overflow-hidden border transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-1 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-md'}`}
                                    >
                                        {/* Image */}
                                        <div className="relative md:w-[55%] overflow-hidden" style={{ minHeight: '280px', aspectRatio: '16/9' }}>
                                            <img
                                                src={`${API_URL}/${featuredBlog.image}`}
                                                alt={featuredBlog.title}
                                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                            />
                                            <div className="absolute top-4 left-4">
                                                <span className="px-3 py-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg">
                                                    À la une
                                                </span>
                                            </div>
                                            {isAdmin && (
                                                <div className="absolute top-4 right-4 flex gap-2">
                                                    <button
                                                        onClick={(e) => { e.preventDefault(); window.location.href = `/modifier-article/${featuredBlog.id}`; }}
                                                        className="p-2 rounded-full backdrop-blur-md bg-black/40 text-white hover:bg-amber-500 transition-colors"
                                                    >
                                                        <AiFillEdit size={14} />
                                                    </button>
                                                    <button
                                                        onClick={(e) => { e.preventDefault(); deleteBlog(featuredBlog.id); }}
                                                        className="p-2 rounded-full backdrop-blur-md bg-black/40 text-white hover:bg-red-600 transition-colors"
                                                    >
                                                        <AiFillDelete size={14} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content */}
                                        <div className={`md:w-[45%] p-8 md:p-10 flex flex-col justify-between ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            <div>
                                                <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {formatDate(featuredBlog.createdAt)}
                                                </span>
                                                <h2 className={`mt-3 text-2xl md:text-[1.65rem] font-bold leading-snug line-clamp-3 transition-colors ${isDark ? 'group-hover:text-blue-400' : 'group-hover:text-blue-600'}`}>
                                                    <SafeContent htmlContent={featuredBlog.title} />
                                                </h2>
                                                <p className={`mt-4 text-sm leading-relaxed line-clamp-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    <SafeContent htmlContent={featuredBlog.description} />
                                                </p>
                                            </div>
                                            <div className={`mt-6 pt-5 border-t flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                                                <span className="px-5 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors inline-block">
                                                    Lire l'article →
                                                </span>
                                                <div className="flex items-center gap-3">
                                                    {(featuredBlog.views || 0) > 0 && (
                                                        <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                            <FaEye size={10} /> {featuredBlog.views}
                                                        </span>
                                                    )}
                                                    <button
                                                        onClick={(e) => { e.preventDefault(); toggleLike(featuredBlog.id); }}
                                                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-all ${
                                                            likedBlogs.has(featuredBlog.id)
                                                                ? 'bg-red-50 border-red-200 text-red-500'
                                                                : isDark
                                                                    ? 'border-gray-700 text-gray-400 hover:border-red-700 hover:text-red-400'
                                                                    : 'border-gray-200 text-gray-400 hover:border-red-300 hover:text-red-400'
                                                        }`}
                                                    >
                                                        <AiFillHeart size={12} /> {featuredBlog.likes || 0}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            )}

                            {/* Section heading */}
                            {gridBlogs.length > 0 && (
                                <div className="flex items-center gap-4 mb-7">
                                    <h2 className={`text-lg font-bold tracking-tight shrink-0 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {page === 1 && featuredBlog ? 'Autres articles' : 'Tous les articles'}
                                    </h2>
                                    <div className={`flex-1 h-px ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />
                                </div>
                            )}

                            {/* Articles grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                {gridBlogs.map((blog, i) => (
                                    <motion.div
                                        key={blog.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: i * 0.07 }}
                                        className="group"
                                    >
                                        <Link
                                            to={`/blog/${blog.id}`}
                                            className={`flex flex-col h-full rounded-2xl overflow-hidden border transition-all duration-300 group-hover:shadow-xl group-hover:-translate-y-1 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}
                                        >
                                            {/* Image */}
                                            <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
                                                <img
                                                    src={`${API_URL}/${blog.image}`}
                                                    alt={blog.title}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                />
                                                {/* Action buttons */}
                                                <div className="absolute top-3 right-3 flex gap-1.5 z-10">
                                                    <button
                                                        onClick={(e) => { e.preventDefault(); toggleLike(blog.id); }}
                                                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold backdrop-blur-md transition-all shadow ${
                                                            likedBlogs.has(blog.id)
                                                                ? 'bg-red-500 text-white'
                                                                : 'bg-black/40 text-white hover:bg-red-500'
                                                        }`}
                                                    >
                                                        <AiFillHeart size={10} /> {blog.likes || 0}
                                                    </button>
                                                    {isAdmin && (
                                                        <>
                                                            <button
                                                                onClick={(e) => { e.preventDefault(); window.location.href = `/modifier-article/${blog.id}`; }}
                                                                className="p-1.5 rounded-full backdrop-blur-md bg-black/40 text-white hover:bg-amber-500 transition-colors shadow"
                                                            >
                                                                <AiFillEdit size={10} />
                                                            </button>
                                                            <button
                                                                onClick={(e) => { e.preventDefault(); deleteBlog(blog.id); }}
                                                                className="p-1.5 rounded-full backdrop-blur-md bg-black/40 text-white hover:bg-red-600 transition-colors shadow"
                                                            >
                                                                <AiFillDelete size={10} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Card body */}
                                            <div className="flex flex-col flex-1 p-5">
                                                <span className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                    {formatDate(blog.createdAt)}
                                                </span>
                                                <h3 className={`text-base font-bold leading-snug line-clamp-2 mb-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-400' : 'text-gray-900 group-hover:text-blue-600'}`}>
                                                    <SafeContent htmlContent={blog.title} />
                                                </h3>
                                                <p className={`text-sm line-clamp-3 flex-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    <SafeContent htmlContent={blog.description} />
                                                </p>
                                                <div className={`mt-4 pt-3 border-t flex items-center justify-between ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                                                    <span className={`text-xs font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                                        Lire l'article →
                                                    </span>
                                                    {(blog.views || 0) > 0 && (
                                                        <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                            <FaEye size={10} /> {blog.views}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Pagination — correctly inside main column */}
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center gap-2 mt-12">
                                    <button
                                        onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        disabled={page === 1}
                                        className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all border ${page === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-blue-50 dark:hover:bg-gray-800'} ${isDark ? 'text-gray-300 border-gray-700' : 'text-gray-600 border-gray-200'}`}
                                    >
                                        ← Précédent
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                                        <button
                                            key={n}
                                            onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                            className={`w-10 h-10 rounded-xl text-sm font-bold transition-all ${n === page ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : `border ${isDark ? 'text-gray-300 border-gray-700 hover:bg-gray-800' : 'text-gray-600 border-gray-200 hover:bg-gray-50'}`}`}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                                        disabled={page === totalPages}
                                        className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all border ${page === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-blue-50 dark:hover:bg-gray-800'} ${isDark ? 'text-gray-300 border-gray-700' : 'text-gray-600 border-gray-200'}`}
                                    >
                                        Suivant →
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* ── Sidebar (25%) ── */}
                        <aside className="w-full xl:w-1/4">
                            <div className={`xl:sticky xl:top-28 rounded-3xl border p-6 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100 shadow-sm'}`}>
                                <h2 className={`text-lg font-bold tracking-tight mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    Les plus appréciés
                                </h2>
                                <p className={`text-xs mb-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    Articles plébiscités par nos lecteurs
                                </p>

                                <div className={`flex flex-col divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                                    {popularBlogs.map((blog, i) => (
                                        <Link to={`/blog/${blog.id}`} key={blog.id} className="flex items-center gap-4 py-4 group first:pt-0 last:pb-0">
                                            {/* Rank */}
                                            <span className={`text-xl font-black shrink-0 w-7 ${i === 0 ? 'text-blue-600' : isDark ? 'text-gray-700' : 'text-gray-200'}`}>
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            {/* Thumbnail */}
                                            <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0">
                                                <img
                                                    src={`${API_URL}/${blog.image}`}
                                                    alt={blog.title}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                />
                                            </div>
                                            {/* Info */}
                                            <div className="flex-1 min-w-0">
                                                <h4 className={`text-sm font-bold leading-snug line-clamp-2 transition-colors ${isDark ? 'text-white group-hover:text-blue-400' : 'text-gray-800 group-hover:text-blue-600'}`}>
                                                    <SafeContent htmlContent={blog.title} />
                                                </h4>
                                                <span className={`flex items-center gap-1 mt-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    <AiFillHeart size={9} className="text-red-400" /> {blog.likes || 0} appréciations
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </aside>

                    </div>
                )}
            </main>
        </Box>
    );
};

export default BlogPage;
